import axios from "./axios";

const api = {
  getCalculatedReport(params, accessToken) {
    const config = {
      params: params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/reports", config).then(({ data }) => data);
  },

  getExportReport(params, accessToken) {
    const config = {
      params: params,
      responseType: "arraybuffer",
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/reports/export", config).then(({ data, headers }) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      const defaultFileName = headers["content-disposition"]?.split("filename=")?.[1] ?? "Report data export";
      link.href = url;
      link.setAttribute("download", defaultFileName);
      document.body.appendChild(link);
      link.click();
      return true;
    });
  },

  updateTurnover({ reportId, turnover }, accessToken) {
    const headers = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios.put(`/reports/${reportId}/turnover`, { turnover: turnover }, headers).then(({ data }) => data);
  },

  getSuppliersDataReport(params, accessToken) {
    const config = {
      params: params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/reports/supplier-questionnaires", config).then(({ data }) => data);
  },

  getDimensionsReport({ reportId, dimensionItemIds }, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`/reports/${reportId}/dimension-items?${dimensionItemIds}`, config).then(({ data }) => data);
  },

  getAggregatedSuppliersDataReport(params, accessToken) {
    const config = {
      params: params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/supplier-questionnaires/aggregation", config).then(({ data }) => data);
  },

  exportScopeOneAndTwoTotalData(params, accessToken) {
    const config = {
      params: params,
      responseType: "arraybuffer",
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/reports/total/export", config).then(({ data, headers }) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      const defaultFileName =
        headers["content-disposition"]?.split("filename=")?.[1] ?? "Scope one and two total data export";
      link.href = url;
      link.setAttribute("download", defaultFileName);
      document.body.appendChild(link);
      link.click();
      return true;
    });
  },

  exportScopeOneAndTwoDimensionsData({ reportId, dimensionItemIds }, accessToken) {
    const config = {
      responseType: "arraybuffer",
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/dimension-items/export?${dimensionItemIds}`, config)
      .then(({ data, headers }) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        const defaultFileName =
          headers["content-disposition"]?.split("filename=")?.[1] ?? "Scope one and two dimensions data export";
        link.href = url;
        link.setAttribute("download", defaultFileName);
        document.body.appendChild(link);
        link.click();
        return true;
      });
  },

  exportSuppliersAggregatedData(params, accessToken) {
    const config = {
      params: params,
      responseType: "arraybuffer",
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/supplier-questionnaires/export", config).then(({ data, headers }) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      const defaultFileName =
        headers["content-disposition"]?.split("filename=")?.[1] ?? "Suppliers aggregated data export";
      link.href = url;
      link.setAttribute("download", defaultFileName);
      document.body.appendChild(link);
      link.click();
      return true;
    });
  },

  getEstimatedPurchasedGoodsAndServices({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/scope-three/estimated-purchased-goods-and-services/items`, config)
      .then(({ data }) => data);
  },

  getCapitalGoods({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get(`/reports/${reportId}/scope-three/capital-goods/items`, config).then(({ data }) => data);
  },

  getUpstreamTransportationAndDistribution({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/scope-three/upstream-transportation-distribution/items`, config)
      .then(({ data }) => data);
  },

  getWasteGeneratedInOperations({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/scope-three/waste-generated-in-operations/items`, config)
      .then(({ data }) => data);
  },

  getDownstreamTransportationAndDistribution({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/scope-three/downstream-transportation-distribution/items`, config)
      .then(({ data }) => data);
  },

  getTreatmentOfSoldProducts({ reportId, params }, accessToken) {
    const config = {
      params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/${reportId}/scope-three/treatment-of-sold-products/items`, config)
      .then(({ data }) => data);
  },

  getCompanyTotalEmissionsReports(params, accessToken) {
    const config = { params, headers: { Authorization: `Bearer ${accessToken}` } };
    return axios.get(`/reports/total-emissions`, config).then(({ data }) => data);
  },

  getCompanyDimensionReports({ dimensionId, params }, accessToken) {
    const config = { params, headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(`/reports/dimensions/${dimensionId}/dimension-items/total-emissions`, config)
      .then(({ data }) => data);
  },

  getQuestionnairesNotes(params, accessToken) {
    const config = { params, headers: { Authorization: `Bearer ${accessToken}` } };
    return axios.get(`/reports/notes`, config).then(({ data }) => data);
  },

  getLastThreeYearsEmissions({ dimensionId, params }, accessToken) {
    const config = { params, headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(`/reports/dimensions/${dimensionId}/dimension-items/last-three-years-total-emissions`, config)
      .then(({ data }) => data);
  },

  getLastThreeYearsEmissionsItemsCount(dimensionId, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(`/reports/dimensions/${dimensionId}/dimension-items/last-three-years-count`, config)
      .then(({ data }) => data);
  },

  getSupplierEngagementReports({ categoryId, supplierIds }, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    const params = [];
    if (categoryId) params.push(`categoryIds=${categoryId}`);
    if (supplierIds) params.push(`supplierIds=${supplierIds}`);
    const queryString = params.length ? `?${params.join("&")}` : "";
    return axios.get(`/suppliers/reports/engagement${queryString}`, config).then(({ data }) => data);
  },

  getNumberOfSupplierQuestionnairesByStatus(categoryId, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(
        `/suppliers/reports/engagement/questionnaires-by-status${
          categoryId && categoryId !== "" ? `?categoryId=${categoryId}` : ""
        }`,
        config
      )
      .then(({ data }) => data);
  },

  getNumberOfFinalisedSupplierQuestionnairesByType(categoryId, accessToken) {
    const config = {
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(
        `/suppliers/reports/engagement/questionnaires-by-type${
          categoryId && categoryId !== "" ? `?categoryId=${categoryId}` : ""
        }`,
        config
      )
      .then(({ data }) => data);
  },

  getSupplierEmissionsReport({ supplierId, params }, accessToken) {
    const config = { params, headers: { Authorization: `Bearer ${accessToken}` } };
    return axios.get(`/suppliers/reports/engagement/${supplierId}/emissions`, config).then(({ data }) => data);
  },

  getSupplierEmissionsPerYear(supplierId, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios.get(`/suppliers/reports/engagement/${supplierId}/emissions-per-year`, config).then(({ data }) => data);
  },

  getSuppliersSpends(categoryId, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(
        `/suppliers/reports/engagement/spends${categoryId && categoryId !== "" ? `?categoryId=${categoryId}` : ""}`,
        config
      )
      .then(({ data }) => data);
  },

  getSuppliersTotalSpends(categoryId, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(
        `/suppliers/reports/engagement/spends/total${
          categoryId && categoryId !== "" ? `?categoryId=${categoryId}` : ""
        }`,
        config
      )
      .then(({ data }) => data);
  },

  getTopFiveInactiveSuppliers(categoryId, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(
        `/suppliers/reports/engagement/spends/top-five-inactive${
          categoryId && categoryId !== "" ? `?categoryId=${categoryId}` : ""
        }`,
        config
      )
      .then(({ data }) => data);
  },

  getSuppliersSpendsByLocation({ year, categoryId, supplierId }, accessToken) {
    const config = { headers: { Authorization: `Bearer ${accessToken}` } };
    return axios
      .get(
        `/suppliers/reports/engagement/spends/total/by-location${
          year && year !== "" ? `?year=${year}` : ""
        }${categoryId && categoryId !== "" ? `&categoryId=${categoryId}` : ""}
      ${supplierId && supplierId !== "" ? `&supplierId=${supplierId}` : ""}`,
        config
      )
      .then(({ data }) => data);
  },

  exportConsumptionsData({ dimensionId, queryParams }, accessToken) {
    const config = {
      responseType: "arraybuffer",
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios
      .get(`/reports/dimension/${dimensionId}/consumptions-emissions/export?${queryParams}`, config)
      .then(({ data, headers }) => {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement("a");
        const defaultFileName = headers["content-disposition"]?.split("filename=")?.[1] ?? "Consumptions data export";
        link.href = url;
        link.setAttribute("download", defaultFileName);
        document.body.appendChild(link);
        link.click();
        return true;
      });
  },

  getReportId(params, accessToken) {
    const config = {
      params: params,
      headers: { Authorization: `Bearer ${accessToken}` },
    };
    return axios.get("/reports/identify", config).then(({ data }) => data);
  },
};

export default api;